import React, { useState, useEffect, useRef } from 'react';
import { ExternalLink, CheckCircle, ChevronLeft, ChevronRight, Search, Filter, BookOpen, X, Trophy, Target, Sparkles } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useProgress } from '../context/ProgressContext';

const DsaQuestions = () => {
  const { user } = useAuth();
  const { solvedQuestions, toggleSolved } = useProgress();
  const [filterTopic, setFilterTopic] = useState('All');
  const [filterDifficulty, setFilterDifficulty] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [solvedCountInCat, setSolvedCountInCat] = useState(0);

  const handleToggleSolved = (id, topic) => {
    const wasSolved = solvedQuestions.has(id);
    toggleSolved(id, 'dsa'); // This updates the global set
    setSolvedCountInCat(prev => wasSolved ? Math.max(0, prev - 1) : prev + 1);
  };
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 10;

  const debounceRef = useRef(null);

  const topics = ['All', 'Array', 'String', 'Linked List', 'Tree', 'Stack', 'Queue', 'DP', 'Graph', 'Binary Search', 'Heap', 'Greedy', 'Backtracking'];
  const difficulties = ['All', 'Easy', 'Medium', 'Hard'];

  const fetchQuestions = async () => {
    setLoading(true);
    try {
      const res = await api.getDsaQuestions({ page, limit, topic: filterTopic, difficulty: filterDifficulty, userId: user?.id });
      const data = await res.json();
      if (res.ok) {
        console.log(`[DSA fetch] Total: ${data.total}, SolvedInCat: ${data.solvedInCategory}`);
        setQuestions(data.data);
        setTotalPages(data.totalPages || 1);
        setTotalQuestions(data.total || 0);
        setSolvedCountInCat(data.solvedInCategory || 0);
      }
    } catch (error) {
      console.error('Failed to fetch questions:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchQuestions();
    }, 300);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filterTopic, filterDifficulty]);

  useEffect(() => {
    setPage(1);
  }, [filterTopic, filterDifficulty]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [page]);

  const getDifficultyStyle = (difficulty) => {
    switch(difficulty) {
      case 'Easy': return { bg: 'bg-emerald-50 dark:bg-emerald-900/10', text: 'text-emerald-600 dark:text-emerald-400', border: 'border-emerald-200 dark:border-emerald-800/50', dot: 'bg-emerald-500' };
      case 'Medium': return { bg: 'bg-amber-50 dark:bg-amber-900/10', text: 'text-amber-600 dark:text-amber-400', border: 'border-amber-200 dark:border-amber-800/50', dot: 'bg-amber-500' };
      case 'Hard': return { bg: 'bg-red-50 dark:bg-red-900/10', text: 'text-red-600 dark:text-red-400', border: 'border-red-200 dark:border-red-800/50', dot: 'bg-red-500' };
      default: return { bg: 'bg-gray-50', text: 'text-gray-600', border: 'border-gray-200', dot: 'bg-gray-400' };
    }
  };

  const filteredQuestions = questions.filter(q => 
    q.title.toLowerCase().includes(searchQuery.toLowerCase())
  );


  return (
    <div className="flex flex-col lg:flex-row gap-8 items-start">
      {/* ═══ Sidebar Filter (Desktop) / Horizontal Chips (Mobile) ═══ */}
      <aside className="w-full lg:w-72 flex-shrink-0 space-y-6 lg:sticky lg:top-24 lg:h-[calc(100vh-120px)] overflow-y-auto pb-10 pr-2 custom-scrollbar">
        <div className="glass-card p-6 space-y-8">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-indigo-500 flex items-center justify-center shadow-lg shadow-indigo-500/20">
              <Filter className="w-4 h-4 text-white" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white font-display uppercase tracking-wider text-sm">Filter by Category</h3>
          </div>

          {/* Search Box */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input 
              type="text"
              placeholder="Search problems..."
              className="input-field pl-10 pr-4 py-2.5 text-sm w-full bg-gray-50/50 dark:bg-gray-800/50 focus:bg-white dark:focus:bg-gray-800 transition-all border-gray-200 dark:border-gray-700"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Topic Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest">Topic</p>
              <span className="text-[10px] font-medium text-indigo-500 bg-indigo-50 dark:bg-indigo-900/30 px-2 py-0.5 rounded-full">
                {filterTopic}
              </span>
            </div>
            <div className="flex flex-wrap lg:flex-col gap-2 no-scrollbar overflow-x-auto lg:overflow-visible pb-2 lg:pb-0">
              {topics.map(topic => (
                <button
                  key={topic}
                  onClick={() => setFilterTopic(topic)}
                  className={`whitespace-nowrap px-4 py-2.5 rounded-xl text-sm font-semibold transition-all duration-300 text-left flex items-center justify-between group ${
                    filterTopic === topic 
                      ? 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow-lg shadow-indigo-500/20' 
                      : 'bg-gray-50 dark:bg-gray-800/40 text-gray-600 dark:text-gray-400 hover:bg-white dark:hover:bg-gray-800 hover:text-indigo-600 border border-transparent hover:border-indigo-100 dark:hover:border-indigo-900/50'
                  }`}
                >
                  {topic}
                  {filterTopic === topic && (
                    <Sparkles className="w-3 h-3 text-white animate-pulse" />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Difficulty Section */}
          <div className="space-y-4 pt-4 border-t border-gray-100 dark:border-gray-800/50">
            <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest">Difficulty</p>
            <div className="flex flex-wrap lg:flex-col gap-2">
              {difficulties.map(diff => (
                <button
                  key={diff}
                  onClick={() => setFilterDifficulty(diff)}
                  className={`px-4 py-2.5 rounded-xl text-sm font-semibold transition-all duration-300 text-left flex items-center gap-3 ${
                    filterDifficulty === diff 
                      ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900' 
                      : 'bg-gray-50 dark:bg-gray-800/40 text-gray-600 dark:text-gray-400 hover:bg-white dark:hover:bg-gray-800 border border-transparent hover:border-gray-200 dark:hover:border-gray-700'
                  }`}
                >
                  <div className={`w-2 h-2 rounded-full ${getDifficultyStyle(diff).dot}`} />
                  {diff}
                </button>
              ))}
            </div>
          </div>
        </div>
      </aside>

      {/* ═══ Main Content ═══ */}
      <div className="flex-1 w-full space-y-6">
        {/* Header Title (Mobile only or redundant if preferred) */}
        <div className="flex items-center gap-4 mb-2">
          <BookOpen className="w-8 h-8 text-indigo-500 lg:hidden" />
          <div>
            <h2 className="text-3xl font-extrabold text-gray-900 dark:text-white font-display leading-tight">DSA Mastery</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Curated list of problems to sharpen your coding skills.</p>
          </div>
        </div>

        {/* Stats Row */}
        {user && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 stagger-children">
            <div className="glass-card p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                <Trophy className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{solvedQuestions.size}</p>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Solved Today</p>
              </div>
            </div>
            <div className="glass-card p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
                <Target className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{totalQuestions}</p>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Available Questions</p>
              </div>
            </div>
            {/* Minimal Progress Line */}
            <div className="glass-card p-4 flex flex-col justify-center">
              <div className="flex justify-between items-center mb-2">
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Category Mastery</span>
                <span className="text-xs font-bold text-indigo-500">
                  {Math.round((solvedCountInCat / (totalQuestions > 0 ? totalQuestions : 1) || 0) * 100)}%
                </span>
              </div>
              <div className="w-full h-1.5 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 transition-all duration-1000" 
                  style={{ width: `${(solvedCountInCat / (totalQuestions > 0 ? totalQuestions : 1) || 0) * 100}%` }}
                />
              </div>
              <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 mt-2 text-right">
                {solvedCountInCat} / {totalQuestions} solved
              </p>
            </div>
          </div>
        )}

        {/* Questions List */}
        <div className="glass-card overflow-hidden">
          {loading ? (
            <div className="flex flex-col items-center justify-center p-20 space-y-4">
              <div className="relative">
                <div className="w-14 h-14 border-4 border-indigo-100 dark:border-indigo-900/50 rounded-full" />
                <div className="absolute inset-0 w-14 h-14 border-4 border-transparent border-t-indigo-500 rounded-full animate-spin" />
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">Fetching challenges...</p>
            </div>
          ) : (
            <>
              <div className="divide-y divide-gray-100 dark:divide-gray-800/50">
                {filteredQuestions.map((q, idx) => {
                  const isSolved = solvedQuestions.has(q.id);
                  const s = getDifficultyStyle(q.difficulty);
                  return (
                    <div 
                      key={q.id} 
                      className={`p-5 sm:px-8 transition-all duration-300 group hover:bg-gray-50/80 dark:hover:bg-gray-800/30 flex items-center gap-6 ${
                        isSolved ? 'bg-emerald-50/20 dark:bg-emerald-900/5' : ''
                      }`}
                      style={{ animationDelay: `${idx * 20}ms` }}
                    >
                      <button 
                        onClick={() => handleToggleSolved(q.id, q.topic)}
                        className={`flex-shrink-0 transition-transform active:scale-90 ${!user ? 'opacity-30 cursor-not-allowed' : 'hover:scale-110'}`}
                        disabled={!user}
                      >
                        <div className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all ${
                          isSolved 
                            ? 'bg-emerald-500 border-emerald-500 shadow-md shadow-emerald-500/30' 
                            : 'border-gray-300 dark:border-gray-600 group-hover:border-indigo-400'
                        }`}>
                          {isSolved && <CheckCircle className="w-4 h-4 text-white" />}
                        </div>
                      </button>

                      <div className="flex-1 min-w-0">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                          <a 
                            href={q.link} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className={`text-base font-bold flex items-center group/link leading-snug ${
                              isSolved 
                                ? 'text-gray-400 dark:text-gray-500' 
                                : 'text-gray-900 dark:text-white hover:text-indigo-600 dark:hover:text-indigo-400'
                            }`}
                          >
                            <span className="truncate">{q.title}</span>
                            <ExternalLink className="w-4 h-4 ml-2 opacity-0 group-hover/link:opacity-100 transition-opacity flex-shrink-0" />
                          </a>
                          <div className="flex items-center gap-3">
                            <span className={`px-2 py-0.5 rounded-lg text-[10px] font-bold border uppercase tracking-tighter ${s.bg} ${s.text} ${s.border}`}>
                              {q.difficulty}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-4 mt-2">
                          <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 flex items-center gap-1.5">
                            <div className="w-1 h-1 rounded-full bg-indigo-400" />
                            {q.topic}
                          </p>
                          <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 flex items-center gap-1.5">
                            <div className="w-1 h-1 rounded-full bg-gray-300 dark:bg-gray-700" />
                            {q.platform || 'Platform N/A'}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {filteredQuestions.length === 0 && (
                <div className="p-20 text-center space-y-4">
                  <div className="w-16 h-16 bg-gray-50 dark:bg-gray-800/50 rounded-2xl flex items-center justify-center mx-auto">
                    <Search className="w-8 h-8 text-gray-300 dark:text-gray-600" />
                  </div>
                  <div>
                    <p className="text-lg font-bold text-gray-900 dark:text-white">No matches found</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Try refining your search or filters.</p>
                  </div>
                </div>
              )}
              
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="p-6 flex items-center justify-between bg-gray-50/50 dark:bg-gray-900/10">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="flex items-center gap-2 text-sm font-bold text-gray-600 dark:text-gray-400 disabled:opacity-30 hover:text-indigo-600 transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Previous
                  </button>
                  <div className="hidden sm:flex items-center gap-2">
                    {[...Array(totalPages)].map((_, i) => {
                      const pageNum = i + 1;
                      // Show page if it's within range of current page
                      if (pageNum === 1 || pageNum === totalPages || (pageNum >= page - 2 && pageNum <= page + 2)) {
                        return (
                          <button
                            key={pageNum}
                            onClick={() => setPage(pageNum)}
                            className={`w-9 h-9 rounded-xl text-sm font-bold transition-all ${
                              page === pageNum
                                ? 'bg-indigo-500 text-white shadow-lg shadow-indigo-500/20'
                                : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      }
                      if (pageNum === page - 3 || pageNum === page + 3) {
                        return <span key={pageNum} className="text-gray-400 px-1 italic">...</span>;
                      }
                      return null;
                    })}
                  </div>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="flex items-center gap-2 text-sm font-bold text-gray-600 dark:text-gray-400 disabled:opacity-30 hover:text-indigo-600 transition-colors"
                  >
                    Next
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DsaQuestions;

